import React, { useState } from "react";
import { aqiMeta, co2Color, pm25Color } from "./aqmHelpers";

const VIEWS = ["AQI Trend", "CO₂ Trend", "PM2.5 Trend", "Room Comparison"];

const AqmIndoorTrendsPanel = ({ d }) => {
  const [view, setView] = useState("AQI Trend");
  const { hourlyTrend, floors } = d;

  const maxAqi = Math.max(...hourlyTrend.map(x => x.aqi));
  const maxCo2 = Math.max(...hourlyTrend.map(x => x.co2));
  const maxPm25 = Math.max(...hourlyTrend.map(x => x.pm25));

  // Flatten all rooms for comparison
  const allRooms = floors.flatMap(f => f.rooms.map(r => ({ ...r, floorName: f.name })));
  const sortedRooms = [...allRooms].sort((a, b) => b.aqi - a.aqi);

  return (
    <div className="aqm-trends-panel">

      <div className="aqm-view-row">
        {VIEWS.map(v => (
          <button key={v} className={`aqm-view-btn ${view === v ? "aqm-view-active" : ""}`} onClick={() => setView(v)}>{v}</button>
        ))}
      </div>

      {/* AQI Trend */}
      {view === "AQI Trend" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">24-Hour Indoor AQI Trend</div>
          <div className="aqm-bar-chart" style={{ height: 200 }}>
            {hourlyTrend.map(pt => {
              const m = aqiMeta(pt.aqi);
              return (
                <div key={pt.t} className="aqm-bar-col">
                  <div className="aqm-bar-val" style={{ fontSize: 8, color: m.color }}>{pt.aqi > 80 ? pt.aqi : ""}</div>
                  <div className="aqm-bar-wrap">
                    <div className="aqm-bar-fill" style={{ height: `${(pt.aqi / maxAqi) * 100}%`, background: m.color }} />
                  </div>
                  <div className="aqm-bar-lbl">{pt.t.split(":")[0]}</div>
                </div>
              );
            })}
          </div>
          <div className="aqm-trend-legend">
            {[[50,"Good"],[100,"Moderate"],[150,"Sensitive"],[200,"Unhealthy"]].map(([v,l]) => (
              <span key={v} style={{ color: aqiMeta(v).color, fontSize: 11 }}>▬ {l}</span>
            ))}
          </div>
          <div className="aqm-trend-note">🔴 Spike detected at 15:00 (AQI 138) — correlates with Meeting Room 3 full occupancy.</div>
        </div>
      )}

      {/* CO₂ Trend */}
      {view === "CO₂ Trend" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">24-Hour CO₂ Trend (ppm)</div>
          <div className="aqm-do-chart">
            {hourlyTrend.map((pt, i) => {
              const col = co2Color(pt.co2);
              return (
                <div key={i} className="aqm-do-col">
                  <div className="aqm-do-bar-wrap">
                    <div className="aqm-do-bar" style={{ height: `${(pt.co2 / maxCo2) * 100}%`, background: col }} />
                  </div>
                  <div className="aqm-do-val" style={{ color: col, fontSize: 8 }}>{pt.co2 > 1000 ? pt.co2 : ""}</div>
                  <div className="aqm-do-lbl">{pt.t.split(":")[0]}</div>
                </div>
              );
            })}
          </div>
          <div className="aqm-limit-lines">
            <span style={{ color: "#22c55e" }}>▬ Good (&lt;800)</span>
            <span style={{ color: "#eab308" }}>▬ Moderate (800–1000)</span>
            <span style={{ color: "#f97316" }}>▬ Poor (1000–1200)</span>
            <span style={{ color: "#ef4444" }}>▬ Critical (&gt;1200)</span>
          </div>
          <div className="aqm-trend-note">⚠ CO₂ consistently exceeds 1000 ppm during 10:00–18:00 — recommend ventilation schedule adjustment.</div>
        </div>
      )}

      {/* PM2.5 Trend */}
      {view === "PM2.5 Trend" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">24-Hour PM2.5 Trend (µg/m³)</div>
          <div className="aqm-bar-chart" style={{ height: 200 }}>
            {hourlyTrend.map(pt => {
              const col = pm25Color(pt.pm25);
              return (
                <div key={pt.t} className="aqm-bar-col">
                  <div className="aqm-bar-val" style={{ fontSize: 8, color: col }}>{pt.pm25 > 40 ? pt.pm25 : ""}</div>
                  <div className="aqm-bar-wrap">
                    <div className="aqm-bar-fill" style={{ height: `${(pt.pm25 / maxPm25) * 100}%`, background: col }} />
                  </div>
                  <div className="aqm-bar-lbl">{pt.t.split(":")[0]}</div>
                </div>
              );
            })}
          </div>
          <div className="aqm-limit-lines">
            <span style={{ color: "#22c55e" }}>▬ Good (&lt;30)</span>
            <span style={{ color: "#eab308" }}>▬ Moderate (30–60)</span>
            <span style={{ color: "#ef4444" }}>▬ High (&gt;60)</span>
          </div>
        </div>
      )}

      {/* Room Comparison */}
      {view === "Room Comparison" && (
        <div className="aqm-chart-card">
          <div className="aqm-section-title">Room AQI Comparison (All Floors)</div>
          <div className="aqm-room-comp-list">
            {sortedRooms.map(r => {
              const m = aqiMeta(r.aqi);
              return (
                <div key={r.id} className="aqm-rc-row">
                  <div className="aqm-rc-name">
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>{r.floorName}</span>
                    <span style={{ fontWeight: 600, color: "#1f2937" }}>{r.name}</span>
                  </div>
                  <div style={{ flex: 1, padding: "0 12px" }}>
                    <div style={{ background: "#f3f4f6", borderRadius: 4, height: 8, overflow: "hidden" }}>
                      <div style={{ width: `${Math.min(100, (r.aqi / 200) * 100)}%`, background: m.color, height: "100%", borderRadius: 4 }} />
                    </div>
                  </div>
                  <span style={{ color: m.color, fontWeight: 700, fontSize: 14, minWidth: 34 }}>{r.aqi}</span>
                  <span className="aqm-badge" style={{ background: m.bg, color: m.text, fontSize: 10 }}>{m.label}</span>
                  <span style={{ fontSize: 11, color: co2Color(r.co2), marginLeft: 6 }}>CO₂ {r.co2}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="aqm-ai-strip">
        🤖 AI: AQI peak at 15:00 daily (avg 135 over 5 days) — meeting room bookings should include mandatory 15-min air-out break after every 2-hour session.
        CO₂ drops 38% on weekends confirming occupancy as primary driver. Recommend occupancy-based HVAC scheduling.
      </div>
    </div>
  );
};

export default AqmIndoorTrendsPanel;
